import type { SubmissionStatus, VideoStatus } from "@growthwise/core";
import { useEffect, useRef, type ReactNode } from "react";
import { useLanguage } from "../i18n/LanguageProvider";

export const Alert = ({
  tone,
  children,
  title,
}: {
  tone: "info" | "success" | "warning" | "danger";
  children: ReactNode;
  title?: string;
}) => (
  <div
    className={`gw-alert gw-alert--${tone}`}
    role={tone === "danger" ? "alert" : "status"}
  >
    <div className="gw-alert__body">
      {title ? (
        <strong style={{ display: "block", marginBottom: "0.2rem" }}>
          {title}
        </strong>
      ) : null}
      {children}
    </div>
  </div>
);

export const Spinner = ({ label }: { label?: string }) => {
  const { t } = useLanguage();
  return (
    <p
      role="status"
      aria-live="polite"
      style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}
    >
      <span className="gw-spinner" aria-hidden="true" />
      {label ?? t("common.loading")}
    </p>
  );
};

export const EmptyState = ({
  children,
  action,
}: {
  children: ReactNode;
  action?: ReactNode;
}) => (
  <div className="gw-empty">
    <p>{children}</p>
    {action ? <div style={{ marginTop: "1rem" }}>{action}</div> : null}
  </div>
);

/**
 * Determinate progress indicator for uploads.
 *
 * `max` can legitimately be zero before the first progress event arrives,
 * so the percentage is guarded rather than dividing by zero.
 */
export const ProgressBar = ({
  value,
  max,
  label,
}: {
  value: number;
  max: number;
  label: string;
}) => {
  const percent = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div>
      <div
        className="gw-progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label={label}
      >
        <div className="gw-progress__bar" style={{ width: `${percent}%` }} />
      </div>
      <p className="gw-note" aria-live="polite">
        {label} · {percent}%
      </p>
    </div>
  );
};

export const SkeletonRows = ({ rows = 3 }: { rows?: number }) => (
  <div aria-hidden="true" style={{ display: "grid", gap: "0.75rem" }}>
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className="gw-skeleton" style={{ height: "3rem" }} />
    ))}
  </div>
);

const SUBMISSION_TONE: Record<SubmissionStatus, string> = {
  draft: "",
  submitted: "gw-badge--info",
  in_review: "gw-badge--warning",
  accepted: "gw-badge--success",
  declined: "gw-badge--danger",
  withdrawn: "",
  archived: "",
};

export const SubmissionStatusBadge = ({
  status,
}: {
  status: SubmissionStatus;
}) => {
  const { t } = useLanguage();
  return (
    <span className={`gw-badge ${SUBMISSION_TONE[status]}`}>
      {t(`submissionStatus.${status}`)}
    </span>
  );
};

const VIDEO_TONE: Record<VideoStatus, string> = {
  pending_upload: "gw-badge--warning",
  uploaded: "gw-badge--success",
  upload_failed: "gw-badge--danger",
  delete_pending: "gw-badge--warning",
  deleted: "",
};

export const VideoStatusBadge = ({ status }: { status: VideoStatus }) => {
  const { t } = useLanguage();
  return (
    <span className={`gw-badge ${VIDEO_TONE[status]}`}>
      {t(`videoStatus.${status}`)}
    </span>
  );
};

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  destructive?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation before a destructive action.
 *
 * Every delete in the product goes through this: the brief requires an
 * explicit confirmation, and an accidental tap on a phone should not be
 * able to destroy an application or a recording.
 *
 * Focus moves to the dialog on open, Escape cancels, and focus returns to
 * the triggering element on close.
 */
export const ConfirmDialog = ({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive = true,
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>("button")?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !pending) onCancel();

      // Keep Tab inside the dialog: a keyboard user must not be able to
      // reach the page behind an open confirmation.
      if (event.key === "Tab") {
        const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) return;

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus();
    };
  }, [onCancel, open, pending]);

  if (!open) return null;

  return (
    <div
      className="gw-modal-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget && !pending) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        className="gw-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-body"
      >
        <h2 id="confirm-title" style={{ fontSize: "var(--gw-text-lg)" }}>
          {title}
        </h2>
        <p id="confirm-body" style={{ marginTop: "0.75rem" }}>
          {body}
        </p>
        <div className="gw-form-actions gw-form-actions--end">
          <button
            type="button"
            className="gw-btn gw-btn--secondary"
            onClick={onCancel}
            disabled={pending}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`gw-btn ${destructive ? "gw-btn--danger" : "gw-btn--primary"}`}
            onClick={onConfirm}
            disabled={pending}
            aria-busy={pending}
          >
            {pending ? (
              <span className="gw-spinner" aria-hidden="true" />
            ) : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

interface PaginationProps {
  page: number;
  hasNext: boolean;
  hasPrevious: boolean;
  onNext: () => void;
  onPrevious: () => void;
}

/**
 * Cursor pagination controls.
 *
 * Forward-only cursors mean there is no total count and no page numbers to
 * jump to; that is a deliberate trade so an admin list never triggers an
 * unbounded count over the whole collection.
 */
export const Pagination = ({
  page,
  hasNext,
  hasPrevious,
  onNext,
  onPrevious,
}: PaginationProps) => {
  const { t } = useLanguage();
  return (
    <nav className="gw-pagination" aria-label={t("admin.page", { page })}>
      <button
        type="button"
        className="gw-btn gw-btn--secondary gw-btn--sm"
        onClick={onPrevious}
        disabled={!hasPrevious}
      >
        {t("admin.previous")}
      </button>
      <span className="gw-note">{t("admin.page", { page })}</span>
      <button
        type="button"
        className="gw-btn gw-btn--secondary gw-btn--sm"
        onClick={onNext}
        disabled={!hasNext}
      >
        {t("admin.next")}
      </button>
    </nav>
  );
};

/**
 * Honest empty state for an unimplemented region adapter.
 *
 * Shown instead of a form when the configured deployment region has no
 * provider. It names the region and says plainly that the interface exists
 * but the integration does not.
 */
export const NotConfiguredPanel = ({ region }: { region: string }) => {
  const { t } = useLanguage();
  return (
    <div className="gw-card" role="status">
      <span className="gw-badge gw-badge--warning">
        {t("notConfigured.title")}
      </span>
      <p style={{ marginTop: "0.75rem" }}>
        {t("notConfigured.body", { region })}
      </p>
      <p className="gw-note" style={{ marginTop: "0.5rem" }}>
        {t("notConfigured.region", { region })}
      </p>
    </div>
  );
};
